import { useContext, useEffect, useState, useRef } from 'react';
import { UserContext } from '../context/UserContext';
import api from '../api';
import { MaintenanceAPI } from '../api';
import './home.css';

// Konfiguration / Schwellenwerte zentral
const MIN_POINTS = 50000;  // Mindestens 50.000 All-Time-Punkte
const MIN_WEEKS = 8;       // Mindestens 8 Wochen Mitgliedschaft
const MIN_INVITED = 2;     // Mindestens 2 aktive neue Anwärter/Creator

export default function MentorProgramService() {
  const { user } = useContext(UserContext);
  const [allowed, setAllowed] = useState(true);
  const [checkingMaint, setCheckingMaint] = useState(true);
  const isAdmin = Array.isArray(user?.roles) && (user.roles.includes('admin') || user.roles.includes('owner'));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await MaintenanceAPI.status();
        const maintOn = !!s?.enabled;
        if (active) {
          if (maintOn && !isAdmin) setAllowed(false); else setAllowed(true);
        }
      } catch { if (active) setAllowed(true); } finally { if (active) setCheckingMaint(false); }
    })();
    return () => { active = false; };
  }, [isAdmin]);

  // Popup Formular Sichtbarkeit
  const [openForm, setOpenForm] = useState(false);
  const dialogRef = useRef(null);

  // Automatische Eligibility Checks
  const [eligibility, setEligibility] = useState({
    membershipWeeksOk: false,
    pointsOk: false,
    invitedOk: false,
    loading: true,
  });

  useEffect(() => {
    if (!user) { setEligibility(e => ({ ...e, loading: false })); return; }
    let active = true;
    (async () => {
      try {
        // Versuche optionalen Endpunkt (falls später implementiert)
        // Fallback lokale Berechnung.
        let invitedCount = user.invitedCreatorsActive || user.invitedActiveCount || 0;
        let allTimePoints = user.allTimePoints || user.pointsAllTime || user.totalPoints || 0;
        let createdAt = user.createdAt || user.joinedAt || user.registrationDate;
        let membershipWeeks = 0;
        if (createdAt) {
          const diffMs = Date.now() - new Date(createdAt).getTime();
          membershipWeeks = diffMs / (1000 * 60 * 60 * 24 * 7);
        }
        // Versuche API für genauere Daten
        try {
          const res = await api.get('/mentor/eligibility');
          if (res?.data) {
            if (typeof res.data.invitedActiveCount === 'number') invitedCount = res.data.invitedActiveCount;
            if (typeof res.data.allTimePoints === 'number') allTimePoints = res.data.allTimePoints;
            // Nur überschreiben, wenn sinnvoller (>0) Wert zurückkommt
            if (typeof res.data.membershipWeeks === 'number' && res.data.membershipWeeks > 0) {
              membershipWeeks = res.data.membershipWeeks;
            }
          }
        } catch {}
        if (active) setEligibility({
          membershipWeeksOk: membershipWeeks >= MIN_WEEKS,
          pointsOk: allTimePoints >= MIN_POINTS,
          invitedOk: invitedCount >= MIN_INVITED,
          loading: false,
          membershipWeeks,
          allTimePoints,
          invitedCount,
        });
      } catch {
        if (active) setEligibility(e => ({ ...e, loading: false }));
      }
    })();
    return () => { active = false; };
  }, [user?.id]);

  // Formularzustand
  const [form, setForm] = useState({
    confirmActiveParticipation: false,
    confirmNoRuleBreaches: false,
    why: '',
    supportAreas: [],
    supportOther: '',
    experience: '',
    availability: [],
    intro: '',
    notes: '',
    acceptRules: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const supportOptions = [
    'Stream-Setup / Technik',
    'Content-Planung / Ideen',
    'Plattform-/Punktesystem / Supportpläne',
    'Community-Aufbau / Networking',
    'Auftreten vor der Kamera / Moderation',
    'Social Media / Reichweite',
    'Sonstiges'
  ];
  const availabilityOptions = [ 'Vormittags', 'Nachmittags', 'Abends', 'Wochenenden', 'flexibel / nach Absprache' ];

  function toggleArrayItem(key, value) {
    setForm(f => {
      const arr = f[key];
      const exists = arr.includes(value);
      return { ...f, [key]: exists ? arr.filter(v => v !== value) : [...arr, value] };
    });
  }

  function validate() {
    const errs = {};
    if (!eligibility.membershipWeeksOk || !eligibility.pointsOk || !eligibility.invitedOk) {
      errs.eligibility = 'Automatische Voraussetzungen sind nicht erfüllt.';
    }
    if (!form.confirmActiveParticipation) errs.confirmActiveParticipation = 'Bestätigung erforderlich.';
    if (!form.confirmNoRuleBreaches) errs.confirmNoRuleBreaches = 'Bestätigung erforderlich.';
    if (!form.why.trim()) errs.why = 'Bitte Motivation angeben (Pflichtfeld).';
    if (form.supportAreas.length === 0) errs.supportAreas = 'Mindestens ein Bereich wählen.';
    if (form.supportAreas.includes('Sonstiges') && !form.supportOther.trim()) errs.supportOther = 'Bitte Sonstiges präzisieren.';
    if (!form.intro.trim()) errs.intro = 'Kurzvorstellung benötigt.';
    if (!form.acceptRules) errs.acceptRules = 'Einverständnis mit Mentoren-Regeln erforderlich.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true); setSubmitSuccess(false); setSubmitMessage('');
    try {
      const payload = {
        why: form.why.trim(),
        supportAreas: form.supportAreas,
        supportOther: form.supportAreas.includes('Sonstiges') ? form.supportOther.trim() : '',
        experience: form.experience.trim(),
        availability: form.availability,
        intro: form.intro.trim(),
        notes: form.notes.trim(),
        confirmActiveParticipation: form.confirmActiveParticipation,
        confirmNoRuleBreaches: form.confirmNoRuleBreaches,
        acceptRules: form.acceptRules,
      };
      const res = await api.post('/mentor/apply', payload);
      setSubmitSuccess(true);
      setSubmitMessage(res?.data?.message || 'Danke! Wir prüfen deine Bewerbung und melden uns zeitnah bei dir.');
      // Optional: Reset
      setForm(f => ({ ...f, why:'', supportAreas:[], supportOther:'', experience:'', availability:[], intro:'', notes:'', acceptRules:false }));
    } catch (err) {
      setSubmitSuccess(false);
      setSubmitMessage(err?.response?.data?.error || 'Fehler beim Absenden. Bitte später erneut versuchen.');
    } finally {
      setSubmitting(false);
    }
  }

  // ESC schließt Modal
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && openForm) setOpenForm(false); }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [openForm]);

  if (checkingMaint) return null;
  if (!allowed) {
    return <div style={{padding:'40px 20px', textAlign:'center'}}><h2 style={{marginTop:0}}>Vorübergehend nicht verfügbar</h2><p>Diese Seite ist im Wartungsmodus nur für Administratoren sichtbar.</p></div>;
  }

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Mentoren‑Programm</h2>
        <p className="hero-desc" style={{ textAlign:'center', margin:'0 auto 24px', maxWidth:760 }}>Begleitung. Orientierung. Starker Start.</p>
      </section>
      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 80px', color:'#fff' }}>
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Warum Mentoren so wichtig sind</h2>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Mentoren sind ein freiwilliger, aber zentraler Baustein im AP‑Network. Sie helfen neuen oder weniger erfahrenen Creatorn dabei, schnell anzukommen, die Systeme zu verstehen und sich in der Community wohlzufühlen. Mentoring ist bewusst menschlich, praxisnah und auf Augenhöhe gedacht – als Ergänzung zu unseren automatisierten Support- und Belohnungssystemen.</p>
        </div>
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Voraussetzungen: Diese Kriterien musst du erfüllen</h2>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Wenn du Mentor werden möchtest, solltest du bereits gezeigt haben, dass du im AP‑Network aktiv, fair und langfristig engagiert bist. Für die Mentorenrolle gelten daher folgende Voraussetzungen:</p>
          {/* Auf der Seite nur auflisten, keine Häkchen/X anzeigen */}
          <ManualInfo label={`Mindestens ${MIN_POINTS.toLocaleString('de-DE')} All-Time-Punkte`} detail="Damit stellen wir sicher, dass Mentoren bereits nachhaltig Teil des Networks sind und Support aktiv leben." />
          <ManualInfo label={`Mindestens ${MIN_WEEKS} Wochen Mitgliedschaft im AP-Network`} detail="Erst kennenlernen, dann begleiten – Mentoren sollen die Community wirklich verstanden haben." />
          <ManualInfo label={`Mindestens ${MIN_INVITED} neue Anwärter/Creator aktiv geworben`} detail={`Mentoren sind auch Community-Botschafter. Ab ${MIN_INVITED} aktiven Einladungen ist eine Mentor-Freischaltung möglich.`} />
          <ManualInfo label="Aktive Teilnahme am Community-Leben" detail="Support-Aktivität, Lurks/Watchtime, Chat, Events oder Challenges." />
          <ManualInfo label="Positives Community-Verhalten & keine Regelverstöße" detail="Mentoren stehen sichtbar für den Spirit des AP-Network." />
          <ManualInfo label="Erfolgreiche Bewerbung über die Plattform" detail="Mit Kurzprofil und Motivation – damit klar ist, warum du Mentor sein möchtest." />
        </div>
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Was Mentoren tun: Aufgaben & Pflichten</h2>
          <TaskList items={[
            ['Einstieg erleichtern','Neue Mitglieder bei den ersten Schritten begleiten, typische Fragen beantworten und Orientierung geben.'],
            ['Technische & organisatorische Hilfe','Unterstützung beim Stream-Setup, bei Tools (z. B. Lurker-Client) und bei AP-Network-Funktionen.'],
            ['Plattform & Punktesystem erklären','Wie Support, Punkte, Achievements und Network-Abläufe funktionieren – verständlich und praxisnah.'],
            ['Community-Integration fördern','Hilfe bei Events, Challenges, Eintragungen/Erstellungen und beim Finden der richtigen Bereiche.'],
            ['Ansprechpartner sein','Für organisatorische Fragen, Unsicherheiten oder Orientierung im Network.'],
            ['Konstruktives Feedback geben','Freundlich, respektvoll und hilfreich zu Stream, Verhalten oder Auftritt – ohne Druck.']
          ]} />
        </div>
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Grenzen & Fairness (wichtig!)</h2>
          <TaskList items={[
            ['Maximal 3 aktive Mentees gleichzeitig','Qualität vor Quantität.'],
            ['Mentoring-Dauer pro Mentee: max. 4 Wochen','Danach ist das Ziel Selbstständigkeit – Verlängerung ist bei Bedarf möglich.'],
            ['Mentee kann wählen oder zugewiesen werden','Matching soll für beide Seiten passen.'],
            ['Keine 24/7-Verfügbarkeitspflicht','Mentoren helfen verlässlich, aber niemand muss rund um die Uhr erreichbar sein.']
          ]} />
        </div>
        <div className="highlight-card" style={{ padding:24, marginBottom:24 }}>
          <h2 className="highlight-title">Deine Vorteile als Mentor</h2>
          <BenefitGrid items={[
            ['Punktebonus für aktive Betreuung','z. B. +50 Punkte pro Woche je aktiv betreutem Mentee, wenn mindestens 1 dokumentierte Interaktion pro Woche stattfindet.'],
            ['Mentor-Achievements / Level-System','Level 1: 1 betreuter Creator · Level 2: 3 betreute Creator · Level 3: 5+ betreute Creator'],
            ['Sichtbarkeit & offizieller Status','Mentor-Badge im Profil, Mentorenrolle auf Discord und Plattform sowie Listing als Ansprechpartner.'],
            ['Community-Wirkung','Du trägst messbar dazu bei, dass das AP-Network stark, fair und unterstützend bleibt.']
          ]} />
        </div>
        <div className="highlight-card" style={{ padding:24 }}>
          <h2 className="highlight-title">Interesse? Bewirb dich als Mentor</h2>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Wenn du dich in den Voraussetzungen wiederfindest und Lust hast, andere Creator zu begleiten, freuen wir uns auf deine Bewerbung.</p>
          <p style={{ color:'#cbd5e1', lineHeight:1.6 }}>Mentor sein heißt: Erfahrung teilen, Orientierung geben, Network-Spirit leben. Kein Rang, keine Macht – sondern eine Community-Aufgabe mit Herz und Struktur.</p>
          <button className="hero-btn-primary" onClick={()=>setOpenForm(true)} style={{ marginTop:12 }}>Mentor-Bewerbung starten</button>
          {submitSuccess && submitMessage && (
            <div style={{ marginTop:16, padding:'12px 16px', background:'rgba(50,205,137,0.15)', border:'1px solid #32CD89', borderRadius:8, color:'#d8f5e8', whiteSpace:'pre-line' }}>{submitMessage}</div>
          )}
        </div>
      </section>

      {openForm && (
        <div role="dialog" aria-modal="true" className="mentor-modal" ref={dialogRef} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.70)', display:'flex', alignItems:'flex-start', justifyContent:'center', overflowY:'auto', padding:'60px 20px', zIndex:2000 }}>
          <div style={{ width:'100%', maxWidth:760, background:'#0f1f35', border:'1px solid #223', borderRadius:16, padding:28, position:'relative' }}>
            <button aria-label="Schließen" onClick={()=>setOpenForm(false)} style={{ position:'absolute', top:12, right:12, background:'none', border:'none', color:'#fff', fontSize:24, cursor:'pointer' }}>×</button>
            <h2 style={{ marginTop:0 }}>Bewerbung als Mentor</h2>
            <form onSubmit={handleSubmit}>
              {/* Voraussetzung Checkblock */}
              <div style={{ marginBottom:22 }}>
                <div style={{ fontWeight:600, marginBottom:8 }}>Check der Voraussetzungen</div>
                {eligibility.loading && <div style={{ color:'#9ca3af', fontSize:13 }}>Prüfe Voraussetzungen…</div>}
                <EligConfirm label={`Mindestens ${MIN_WEEKS} Wochen Mitglied im AP-Network`} ok={eligibility.membershipWeeksOk} value={(eligibility.membershipWeeks||0).toFixed(1)+" Wochen"} />
                <EligConfirm label={`Mindestens ${MIN_POINTS.toLocaleString('de-DE')} All-Time-Punkte`} ok={eligibility.pointsOk} value={`${eligibility.allTimePoints||0}`} />
                <EligConfirm label={`Mindestens ${MIN_INVITED} aktive Einladungen`} ok={eligibility.invitedOk} value={`${eligibility.invitedCount||0}`} />
                {/* manuelle Bestätigungen */}
                <ManualCheck id="activeParticipation" checked={form.confirmActiveParticipation} onChange={v=>setForm(f=>({...f,confirmActiveParticipation:v}))} label="Ich beteilige mich aktiv am Community-Leben (Support, Chat, Events etc.)." error={errors.confirmActiveParticipation} />
                <ManualCheck id="noBreaches" checked={form.confirmNoRuleBreaches} onChange={v=>setForm(f=>({...f,confirmNoRuleBreaches:v}))} label="Ich hatte in den letzten 8 Wochen keine Regelverstöße." error={errors.confirmNoRuleBreaches} />
                <div style={{ marginTop:10, fontSize:12, color:'#9ca3af' }}>Falls du dir bei einem Punkt unsicher bist, schreib es unten kurz in die Bemerkungen — wir prüfen das gemeinsam.</div>
                {errors.eligibility && <div style={{ color:'#ef4444', fontSize:12, marginTop:8 }}>{errors.eligibility}</div>}
              </div>
              <FieldBlock label="Warum möchtest du Mentor sein?" required error={errors.why}>
                <textarea value={form.why} onChange={e=>setForm(f=>({...f,why:e.target.value}))} rows={5} style={taStyle} placeholder="Was reizt dich an der Rolle? Was möchtest du im AP-Network bewirken? (3–8 Sätze)" />
              </FieldBlock>
              <FieldBlock label="Wobei kannst du Mentees besonders gut unterstützen?" required error={errors.supportAreas}>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {supportOptions.map(opt => (
                    <button type="button" key={opt} onClick={()=>toggleArrayItem('supportAreas', opt)} style={chipStyle(form.supportAreas.includes(opt))}>{opt}</button>
                  ))}
                </div>
                {form.supportAreas.includes('Sonstiges') && (
                  <input type="text" value={form.supportOther} onChange={e=>setForm(f=>({...f,supportOther:e.target.value}))} placeholder="Sonstiges präzisieren" style={{ marginTop:8, ...inputStyle }} />
                )}
                {errors.supportOther && <div style={{ color:'#ef4444', fontSize:12 }}>{errors.supportOther}</div>}
              </FieldBlock>
              <FieldBlock label="Hast du bereits Mentoring/Coaching-Erfahrung? (optional)" error={errors.experience}>
                <textarea value={form.experience} onChange={e=>setForm(f=>({...f,experience:e.target.value}))} rows={3} style={taStyle} placeholder="Falls ja: kurz beschreiben (z. B. in Communitys, Teams, Beruf etc.)." />
              </FieldBlock>
              <FieldBlock label="Typische Zeiten, zu denen du erreichbar bist (optional)">
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {availabilityOptions.map(opt => (
                    <button type="button" key={opt} onClick={()=>toggleArrayItem('availability', opt)} style={chipStyle(form.availability.includes(opt))}>{opt}</button>
                  ))}
                </div>
              </FieldBlock>
              <FieldBlock label="Kurzvorstellung (2–3 Sätze)" required error={errors.intro}>
                <textarea value={form.intro} onChange={e=>setForm(f=>({...f,intro:e.target.value}))} rows={3} style={taStyle} placeholder="Dieser Text kann später im Mentor-Listing sichtbar sein." />
              </FieldBlock>
              <FieldBlock label="Sonstige Bemerkungen / Fragen (optional)">
                <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={3} style={taStyle} />
              </FieldBlock>
              <div style={{ margin:'16px 0' }}>
                <label style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                  <input type="checkbox" checked={form.acceptRules} onChange={e=>setForm(f=>({...f,acceptRules:e.target.checked}))} />
                  <span style={{ fontSize:14 }}>Ich habe die Mentoren-Regeln gelesen (max. 3 Mentees, ca. 4 Wochen Mentoring, keine 24/7-Pflicht) und stimme ihnen zu.</span>
                </label>
                {errors.acceptRules && <div style={{ color:'#ef4444', fontSize:12, marginTop:4 }}>{errors.acceptRules}</div>}
              </div>
              <button type="submit" disabled={submitting} className="hero-btn-primary" style={{ width:'100%', padding:'14px 18px', fontSize:16 }}>
                {submitting ? 'Sende Bewerbung…' : 'Mentor-Bewerbung absenden'}
              </button>
              {submitMessage && !submitSuccess && <div style={{ marginTop:12, color:'#ef4444', fontSize:13 }}>{submitMessage}</div>}
              {submitSuccess && submitMessage && <div style={{ marginTop:12, color:'#32CD89', fontSize:13 }}>{submitMessage}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function EligibilityItem({ ok, loading, label, detail }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <StatusIcon ok={ok} loading={loading} />
        <div style={{ fontWeight:600 }}>{label}</div>
      </div>
      <div style={{ fontSize:13, color:'#9ca3af', paddingLeft:28 }}>{detail}</div>
    </div>
  );
}

function ManualInfo({ label, detail }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:10 }}>
      <div style={{ fontWeight:600 }}>{label}</div>
      <div style={{ fontSize:13, color:'#9ca3af' }}>{detail}</div>
    </div>
  );
}

function TaskList({ items }) {
  return (
    <div style={{ display:'grid', gap:14 }}>
      {items.map(([title, desc], i) => (
        <div key={i} style={{ display:'flex', flexDirection:'column', gap:4, padding:'10px 12px', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, background:'rgba(255,255,255,0.03)' }}>
          <div style={{ fontWeight:600 }}>{title}</div>
          <div style={{ fontSize:13, color:'#cbd5e1', lineHeight:1.5 }}>{desc}</div>
        </div>
      ))}
    </div>
  );
}

function BenefitGrid({ items }) {
  return (
    <div style={{ display:'grid', gap:16, gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))' }}>
      {items.map(([title, desc], i) => (
        <div key={i} style={{ padding:16, border:'1px solid rgba(255,255,255,0.10)', background:'rgba(255,255,255,0.03)', borderRadius:10 }}>
          <div style={{ fontWeight:700, marginBottom:8 }}>{title}</div>
          <div style={{ fontSize:13, color:'#cbd5e1', lineHeight:1.5 }}>{desc}</div>
        </div>
      ))}
    </div>
  );
}

function StatusIcon({ ok, loading }) {
  const baseStyle = { width:20, height:20, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700 };
  if (loading) return <span style={{ ...baseStyle, background:'#555' }}>…</span>;
  return ok
    ? <span style={{ ...baseStyle, background:'#32CD89', color:'#fff' }}>✔</span>
    : <span style={{ ...baseStyle, background:'#e74c3c', color:'#fff' }}>✖</span>;
}

function EligConfirm({ label, ok, value }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
      <StatusIcon ok={ok} loading={false} />
      <div style={{ fontSize:14, fontWeight:600 }}>{label}</div>
      <div style={{ marginLeft:'auto', fontSize:12, color:'#9ca3af' }}>{value}</div>
    </div>
  );
}

function ManualCheck({ id, checked, onChange, label, error }) {
  return (
    <div style={{ marginTop:6 }}>
      <label style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer' }}>
        <input id={id} type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} />
        <span style={{ fontSize:14 }}>{label}</span>
      </label>
      {error && <div style={{ color:'#ef4444', fontSize:12 }}>{error}</div>}
    </div>
  );
}

function FieldBlock({ label, children, required, error }) {
  return (
    <div style={{ marginBottom:20 }}>
      <label style={{ display:'block', fontWeight:600, marginBottom:6 }}>
        {label}{required && <span style={{ color:'#e74c3c' }}> *</span>}
      </label>
      {children}
      {error && <div style={{ color:'#e74c3c', fontSize:12, marginTop:4 }}>{error}</div>}
    </div>
  );
}

const chipStyle = (active) => ({
  padding:'6px 12px',
  borderRadius:999,
  border: active ? '1px solid #32CD89' : '1px solid rgba(255,255,255,0.20)',
  background: active ? 'rgba(50,205,137,0.25)' : 'rgba(255,255,255,0.05)',
  color:'#fff',
  fontSize:12,
  cursor:'pointer'
});
const taStyle = { width:'100%', padding:'10px 12px', borderRadius:8, background:'#14283f', color:'#fff', border:'1px solid #2b425b', resize:'vertical', fontFamily:'inherit' };
const inputStyle = { width:'100%', padding:'8px 12px', borderRadius:8, background:'#14283f', color:'#fff', border:'1px solid #2b425b' };
